/**
 * Pure, injectable bot detection for gating error-tracking initialization.
 *
 * We deliberately do NOT use the server-provided `isBot` flag (unreliable and
 * soft-deprecated). Instead we classify the client from the user-agent string
 * and the WebDriver flag. This is noise reduction, not a security control:
 * sophisticated scrapers spoof the UA and slip through — those are caught later
 * by `errorEventFilter` (origin allowlist + ignore-list). The point here is to
 * avoid initializing RUM at all for the obvious, self-identifying automation
 * that makes up the bulk of the volume.
 */

export interface BotDetectionInput {
  /** Typically `navigator.userAgent`. */
  userAgent?: string;
  /** Typically `navigator.webdriver` (set by Selenium / Puppeteer / Playwright). */
  webdriver?: boolean;
}

/** True if the user-agent matches any of the supplied bot patterns. */
export function isBotUserAgent(userAgent: string | undefined, patterns: RegExp[]): boolean {
  if (!userAgent) { return false; }
  return patterns.some((pattern) => userAgent.match(pattern) !== null);
}

/**
 * True if the client looks like automation. WebDriver is a hard signal; the
 * user-agent is a heuristic match against `patterns`.
 */
export function isLikelyBot(input: BotDetectionInput, patterns: RegExp[]): boolean {
  if (input.webdriver) { return true; }
  return isBotUserAgent(input.userAgent, patterns);
}
